import { useState } from 'react';
import { Link } from 'react-router-dom';
import ComicCard from '@/components/base/ComicCard';
import { comics, updateFeed, fanGroups, genres } from '@/mocks/comics';
import { useSeo } from '@/hooks/useSeo';

const heroBg =
  'https://readdy.ai/api/search-image?query=Stylized%20anime%20illustration%20collage%20of%20manga%20storyboard%20panels%2C%20open%20books%20and%20dynamic%20action%20characters%20in%20motion%2C%20warm%20coral%20orange%20and%20cream%20color%20palette%2C%20vibrant%20pop%20art%20energy%2C%20soft%20bokeh%20light%20effects%2C%20modern%20editorial%20composition%2C%20high%20detail%20digital%20painting&width=1600&height=900&seq=hero-dmzj&orientation=landscape';

const heroSide =
  'https://readdy.ai/api/search-image?query=Anime%20manga%20illustration%20of%20three%20confident%20young%20characters%20standing%20together%2C%20colorful%20coral%20orange%20and%20gold%20lighting%2C%20dynamic%20heroic%20poses%2C%20stylized%20clean%20linework%2C%20vibrant%20modern%20comic%20art%2C%20warm%20tones%2C%20high%20detail&width=700&height=900&seq=hero-side&orientation=portrait';

const faqItems = [
  { q: '如何在动漫之家漫画网快速找到想追的作品？', a: '你既可以通过顶部搜索框直接输入作品名，也可以前往排行榜和分类大全页，按榜单、类型、完结状态筛选，几秒钟就能锁定心仪作品。' },
  { q: '排行榜热门推荐多久更新一次？', a: '热门推荐与排行榜数据会综合近期的阅读热度、收藏量和讨论度持续滚动更新，让真正受读者欢迎的作品始终排在前面。' },
  { q: '完结漫画和连载中漫画有什么区别？', a: '完结漫画已更新至最终话，可以一口气追完不吊胃口；连载中作品则按章节持续更新，我们会在首页显示最新更新动态。' },
  { q: '汉化组独家分类内容从哪来？', a: '动漫之家漫画网与多个优秀汉化组合作，聚合其独家翻译与精校内容，并按热血、恋爱、悬疑等主题整理成独立分类，方便集中浏览。' },
];

export default function Home() {
  useSeo({
    title: '动漫之家漫画网 - 排行榜热门推荐·完结漫画与汉化组分类大全',
    description:
      '动漫之家漫画网聚合排行榜热门推荐、完结漫画与汉化组独家分类内容，按榜单和类型快速找到你想追的作品，一站式逛完动漫之家漫画网排行榜与热门推荐大全。',
    canonicalPath: '/',
  });

  const [activeFaq, setActiveFaq] = useState<number | null>(0);
  const hotList = [...comics].sort((a, b) => b.heat - a.heat).slice(0, 6);
  const ongoing = comics.filter((c) => c.status === '连载中').slice(0, 8);
  const finished = comics.filter((c) => c.status === '已完结').slice(0, 4);

  return (
    <div>
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundImage: `url(${heroBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/70 to-foreground-950/35"></div>
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-16 md:px-6 md:py-20 lg:grid-cols-2">
          <div className="w-full">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-fire-fill"></i> 今日热门持续更新
            </span>
            <h1 className="mt-5 font-heading text-3xl leading-tight text-background-50 md:text-5xl">
              一站逛完动漫之家
              <br />
              <span className="text-accent-400">漫画网</span> 排行榜与大全
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-background-100/90 md:text-lg">
              想看排行榜热门推荐、完结漫画还是汉化组独家内容？这里聚合分类大全，按榜单和类型快速找到你想追的作品，不错过每一部好漫画。
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/ranking"
                className="whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看热门榜
              </Link>
              <Link
                to="/completed"
                className="whitespace-nowrap rounded-lg border border-background-100/40 px-6 py-3 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/10"
              >
                完结漫画精选
              </Link>
            </div>
            <div className="mt-9 flex flex-wrap gap-8">
              <div>
                <p className="font-heading text-2xl text-accent-400">3000+</p>
                <p className="mt-1 text-xs text-background-200/80">收录作品</p>
              </div>
              <div>
                <p className="font-heading text-2xl text-accent-400">120万+</p>
                <p className="mt-1 text-xs text-background-200/80">月活跃读者</p>
              </div>
              <div>
                <p className="font-heading text-2xl text-accent-400">36个</p>
                <p className="mt-1 text-xs text-background-200/80">热门分类</p>
              </div>
            </div>
          </div>
          <div className="hidden w-full lg:block">
            <img
              src={heroSide}
              alt="动漫之家漫画网热门作品角色插画"
              className="mx-auto max-h-[420px] w-auto rounded-2xl object-cover shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Update feed strip */}
      <section className="border-b border-background-200 bg-background-50" aria-label="最新更新">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:px-6">
          <span className="flex shrink-0 items-center gap-2 text-sm font-bold text-primary-600">
            <i className="ri-refresh-line"></i> 今日更新
          </span>
          <div className="flex flex-1 flex-wrap items-center gap-x-6 gap-y-2">
            {updateFeed.map((u) => {
              const comic = comics.find((c) => c.id === u.id);
              if (!comic) return null;
              return (
                <Link
                  key={u.id}
                  to={`/comic/${u.id}`}
                  className="group text-sm text-foreground-600 transition-colors hover:text-primary-600"
                >
                  <span className="font-medium text-foreground-900 group-hover:text-primary-600">{comic.title}</span>
                  <span className="ml-1 text-foreground-500">{u.chapter}</span>
                  <span className="ml-2 text-xs text-foreground-400">{u.time}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hot ranking */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16" aria-label="热门推荐排行榜">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-medium text-secondary-600">
              <i className="ri-fire-line"></i> 人气榜单
            </span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">动漫之家热门推荐排行榜</h2>
            <p className="mt-2 text-sm text-foreground-500">实时综合阅读与收藏热度，挑出最值得追的漫画。</p>
          </div>
          <Link to="/ranking" className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
            完整榜单 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <ol className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {hotList.map((comic, idx) => (
            <li key={comic.id}>
              <Link
                to={`/comic/${comic.id}`}
                className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-3 transition-all hover:border-primary-300 hover:bg-background-50"
              >
                <span
                  className={`font-heading text-2xl w-9 text-center ${
                    idx < 3 ? 'text-primary-500' : 'text-background-400'
                  }`}
                >
                  {idx + 1}
                </span>
                <img
                  src={comic.cover}
                  alt={`${comic.title}排行榜封面`}
                  className="h-16 w-12 rounded-md object-cover"
                />
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-bold text-foreground-950 group-hover:text-primary-600">
                    {comic.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-500">
                    {comic.genre} · {comic.chapters}话
                  </p>
                </div>
                <span className="flex items-center gap-1 text-sm font-semibold text-accent-600">
                  <i className="ri-star-fill text-accent-500"></i>
                  {comic.rating.toFixed(1)}
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </section>

      {/* Ongoing grid */}
      <section className="bg-background-100" aria-label="正在连载">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-600">
                <i className="ri-sparkling-line"></i> 正在热更
              </span>
              <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">连载中 · 追更不停</h2>
              <p className="mt-2 text-sm text-foreground-500">最新章节实时同步，追更体验更丝滑。</p>
            </div>
            <Link to="/genre" className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
              浏览全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
            {ongoing.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </div>
      </section>

      {/* Completed */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16" aria-label="完结漫画">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-medium text-accent-600">
              <i className="ri-check-double-line"></i> 一次追完
            </span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">完结漫画精选</h2>
            <p className="mt-2 text-sm text-foreground-500">已经画上句点的经典，值得一口气看完。</p>
          </div>
          <Link to="/completed" className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
            全部完结漫画 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {finished.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      {/* Genres */}
      <section className="bg-background-100" aria-label="分类大全">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-secondary-600">
              <i className="ri-apps-2-line"></i> 分类大全
            </span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">按类型快速找作品</h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-foreground-500">
              热血、恋爱、玄幻、悬疑……分类标签精准，点击即可浏览对应类型的热门作品。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {genres.filter((g) => g !== '全部').map((g) => (
              <Link
                key={g}
                to={`/genre?type=${encodeURIComponent(g)}`}
                className="group flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-5 text-center transition-all hover:-translate-y-1 hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-100 text-secondary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className="ri-book-2-line text-xl"></i>
                </span>
                <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-600">{g}漫画</span>
                <span className="text-xs text-foreground-400">
                  {comics.filter((c) => c.genre === g || c.tags.includes(g)).length}部
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Fan groups */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16" aria-label="汉化组独家">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-600">
              <i className="ri-team-line"></i> 汉化组独家
            </span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">优质汉化组 · 精校内容</h2>
            <p className="mt-2 text-sm text-foreground-500">聚合多家汉化组的独家翻译与精校作品，分类清晰、出品稳定。</p>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {fanGroups.map((g) => (
            <div key={g.name} className="rounded-lg border border-background-200 bg-background-100 p-5">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className="ri-team-fill"></i>
                </span>
                <div>
                  <h3 className="text-sm font-bold text-foreground-950">{g.name}</h3>
                  <p className="text-xs text-foreground-500">{g.focus}</p>
                </div>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{g.desc}</p>
              <p className="mt-3 flex items-center gap-1 text-xs text-secondary-600">
                <i className="ri-group-line"></i> {g.members} 位同好
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-background-100" aria-label="读者口碑">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-accent-600">
              <i className="ri-chat-heart-line"></i> 读者口碑
            </span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">千万同好的共同选择</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {[
              { name: '追番小能手', role: '排行榜重度用户', text: '首页的排行榜真的准，我追的几部冷门神作都被推出来了，分类也特别细，找作品超快。' },
              { name: '夜猫子漫迷', role: '完结漫画爱好者', text: '专门冲完结漫画来的，一口气追完真的过瘾，更新列表也很清楚，不会错过最终话。' },
              { name: '汉化组观察员', role: '汉化组分类用户', text: '多个汉化组的独家内容集中在一处，翻译质量有保证，不用到处找了，体验很棒。' },
            ].map((t) => (
              <figure key={t.name} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <i className="ri-double-quotes-l text-2xl text-primary-300"></i>
                <blockquote className="mt-2 text-sm leading-relaxed text-foreground-700">{t.text}</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 font-heading text-primary-600">
                    {t.name.slice(0, 1)}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                  <span className="ml-auto flex text-accent-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16" aria-label="常见问题">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-600">
              <i className="ri-question-line"></i> 常见问题
            </span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">使用前你想知道的</h2>
          </div>
          <div className="mt-8 space-y-3">
            {faqItems.map((item, idx) => (
              <div key={item.q} className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
                <button
                  type="button"
                  onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                  aria-expanded={activeFaq === idx}
                >
                  <span className="text-sm font-bold text-foreground-950">{item.q}</span>
                  <i className={`ri-add-line text-lg text-primary-500 transition-transform ${activeFaq === idx ? 'rotate-45' : ''}`}></i>
                </button>
                {activeFaq === idx && (
                  <p className="border-t border-background-200 px-5 py-4 text-sm leading-relaxed text-foreground-600">
                    {item.a}
                  </p>
                )}
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-foreground-500">
            还有疑问？前往 <Link to="/faq" className="font-medium text-primary-600 hover:text-primary-700">常见问题页</Link> 查看更多解答。
          </p>
        </div>
      </section>

      {/* FAQPage JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          }),
        }}
      />
    </div>
  );
}